import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import { componentTypeRegistry } from '../../../../../organisms/ComponentBlueprint/componentTypeRegistry'

function ComponentDetailPanel ({ code, fieldPrefix }) {
  const { control, watch, setValue, getValues } = useFormContext()

  const componentType = watch(`${fieldPrefix}.componentType`)
  const displayName = watch(`${fieldPrefix}.displayName`)
  const description = watch(`${fieldPrefix}.description`)

  const typeEntry = useMemo(() => {
    return componentType ? componentTypeRegistry[componentType] : null
  }, [componentType])

  const ConfigEditor = typeEntry?.configEditor ?? null
  const styleVariantOptions = typeEntry?.styleVariants ?? null

  const [configText, setConfigText] = useState(() => {
    const cfg = getValues(`${fieldPrefix}.config`)
    return cfg ? JSON.stringify(cfg, null, 2) : '{}'
  })
  const [configError, setConfigError] = useState(null)

  useEffect(() => {
    if (ConfigEditor) return
    const cfg = getValues(`${fieldPrefix}.config`)
    setConfigText(cfg ? JSON.stringify(cfg, null, 2) : '{}')
    setConfigError(null)
  }, [fieldPrefix, ConfigEditor, getValues])

  const handleConfigBlur = useCallback(() => {
    try {
      const parsed = JSON.parse(configText)
      setConfigError(null)
      setValue(`${fieldPrefix}.config`, parsed, { shouldDirty: true })
    } catch {
      setConfigError('Invalid JSON')
    }
  }, [configText, fieldPrefix, setValue])

  return (
    <div style={{ paddingLeft: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ marginBottom: '4px' }}>{displayName}</h3>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
          {componentType} &bull; {code}
        </div>
        {description
          ? (
            <p style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              {description}
            </p>
          )
          : null}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Controller
          name={`${fieldPrefix}.displayName`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Display Name'>
              <SydInput {...field} value={field.value || ''} />
            </SydLabel>
          )}
        />
      </div>

      {ConfigEditor
        ? (
          <ConfigEditor fieldPrefix={fieldPrefix} />
        )
        : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <Controller
                name={`${fieldPrefix}.styleVariant`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Style Variant'>
                    {styleVariantOptions
                      ? (
                        <SydSelect
                          options={styleVariantOptions}
                          value={field.value || 'default'}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )
                      : (
                        <SydInput
                          {...field}
                          value={field.value || 'default'}
                        />
                      )}
                  </SydLabel>
                )}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <SydLabel label='Config (JSON)' error={configError}>
                <textarea
                  value={configText}
                  onChange={(e) => setConfigText(e.target.value)}
                  onBlur={handleConfigBlur}
                  rows={15}
                  style={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: configError ? '1px solid var(--color-danger-500)' : '1px solid var(--color-neutral-300)',
                    resize: 'vertical'
                  }}
                />
              </SydLabel>
            </div>
          </>
        )}
    </div>
  )
}

ComponentDetailPanel.propTypes = {
  code: PropTypes.string.isRequired,
  fieldPrefix: PropTypes.string.isRequired
}

export default ComponentDetailPanel
